import React from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";

import { ReactComponent as Logo } from "../../../../assets/svg/logo.svg";
import { ReactComponent as EyeOpen } from "../../../../assets/svg/eye_open.svg";
import { ReactComponent as EyeClose } from "../../../../assets/svg/eye_close.svg";
import { ReactComponent as GoogleIcon } from "../../../../assets/svg/google.svg";
import { ReactComponent as MicrosoftIcon } from "../../../../assets/svg/microsoft.svg";
import { API_URL, forgotPasswordPath } from "../../../../teacherStore";
import { emailRegex } from "../../../../constants/regex";
import styles from "../Login.module.scss";

type Props = {
  login: (email: string, password: string, remember: boolean) => void;
};

const validates: { [key: string]: (value: any) => string } = {};

validates["email"] = val => {
  return !val.email.trim()
    ? "この項目は入力が必須です。"
    : val.email.match(emailRegex)
    ? ""
    : "正しいメールアドレスを入力してください。";
};

validates["password"] = val => {
  return !val.password.trim()
    ? "この項目は入力が必須です。"
    : val.password.length < 6 || val.password.length > 32
    ? "パスワードは6文字以上32文字以内で入力してください。"
    : "";
};

export const LoginForm = (props: Props) => {
  const [isShowPassword, setIsShowPassword] = React.useState(false);
  const [state, setState] = React.useState({
    email: "",
    password: "",
    isChecked: false,
    errorMessages: {} as { [key: string]: string },
  });

  const onSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const errorMessages = {} as { [key: string]: string };
      Object.keys(validates).forEach(key => {
        const ret = validates[key](state);
        if (ret) {
          errorMessages[key] = ret;
        }
      });
      if (Object.keys(errorMessages).length > 0) {
        setState(s => ({ ...s, errorMessages }));
        return;
      }
      props.login(state.email, state.password, state.isChecked);
    },
    [state, props],
  );

  const onChange = React.useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      const val =
        e.currentTarget.type === "checkbox"
          ? e.currentTarget.checked
          : e.currentTarget.value;
      const key = e.currentTarget.name;
      const args = { [key]: val };
      const ret = validates[key] ? validates[key](args) : "";
      setState(prevState => ({
        ...prevState,
        [key]: val,
        errorMessages: { ...prevState.errorMessages, [key]: ret },
      }));
    },
    [],
  );

  return (
    <div className={styles.wrapper}>
      <Logo className={styles.logo} />
      <form className={styles.content} onSubmit={onSubmit}>
        <div className={styles.title}>
          <p className={clsx(styles.primary, styles.en)}>LOGIN</p>
          <p className={styles.secondary}>先生</p>
        </div>
        <div className={styles.inputField}>
          <fieldset>
            <input
              type="email"
              name="email"
              className={state.errorMessages["email"] ? styles.isError : ""}
              id="email"
              value={state.email}
              onChange={onChange}
              onBlur={onChange}
              placeholder="メールアドレスを入力"
            />
            {state.errorMessages["email"] && (
              <p className={styles.errorMessage}>
                {state.errorMessages["email"]}
              </p>
            )}
          </fieldset>
          <fieldset>
            <div className={styles.inputWrapper}>
              <input
                type={isShowPassword ? "text" : "password"}
                id="password"
                name="password"
                className={
                  state.errorMessages["password"] ? styles.isError : ""
                }
                value={state.password}
                onChange={onChange}
                onBlur={onChange}
                placeholder="パスワードを入力"
              />
              <span
                className={styles.eyeIcon}
                onClick={() => {
                  setIsShowPassword(!isShowPassword);
                }}
              >
                {isShowPassword ? <EyeOpen /> : <EyeClose />}
              </span>
            </div>
            {state.errorMessages["password"] && (
              <p className={styles.errorMessage}>
                {state.errorMessages["password"]}
              </p>
            )}
          </fieldset>
        </div>
        <div className={styles.loginOptions}>
          <label className={styles.rememberMe}>
            ログインしたままにする
            <input
              type="checkbox"
              name="isChecked"
              className={styles.checkBox}
              checked={state.isChecked}
              onChange={onChange}
            />
            <span className={styles.customCheckbox} />
          </label>
          <p className={styles.forgotPassword}>
            パスワードがわからない方は
            <Link className={styles.link} to={forgotPasswordPath}>
              こちら
            </Link>
          </p>
        </div>
        <button
          className={styles.submitButton}
          type="submit"
          disabled={
            Object.keys(state.errorMessages).filter(k => state.errorMessages[k])
              .length > 0
          }
        >
          ログイン
        </button>
        <div className={styles.separator}>または</div>
        <div className={styles.socialLogin}>
          <button
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              window.location.href = `${API_URL?.replace(
                "/api/v1/teacher",
                "",
              )}/auth/google_oauth2?kind=dlp_teacher`;
            }}
          >
            <GoogleIcon className={styles.icon} />
            Googleでログイン
          </button>
          <button
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              window.location.href = `${API_URL?.replace(
                "/api/v1/teacher",
                "",
              )}/auth/azure_activedirectory_v2?kind=dlp_teacher&prompt=select_account`;
            }}
          >
            <MicrosoftIcon className={styles.icon} />
            Microsoftでログイン
          </button>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
